import { useState } from "react";
import { format } from "date-fns";
import { Check, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/features/power_projects/components/ui";
import {
  useCompleteTask,
  useCreateTask,
  useDeleteTask,
  useReopenTask,
  type Lead,
} from "@/features/leads/api/leads";

/**
 * Zadania z terminem — „oddzwonić 12.08".
 *
 * Bez nich karta jest pamiętnikiem; z nimi jest narzędziem pracy. Wykonane
 * zjeżdżają na dół i zostają: to, że ktoś już dzwonił i co z tego wyszło, jest
 * częścią historii firmy.
 */

function defaultDue(): string {
  const date = new Date();
  date.setDate(date.getDate() + 3);
  date.setHours(9, 0, 0, 0);
  // datetime-local chce lokalny czas bez strefy
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

export function LeadTasksPanel({ lead }: { lead: Lead }) {
  const createTask = useCreateTask();
  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const deleteTask = useDeleteTask();

  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState(defaultDue());
  const [error, setError] = useState("");

  const open = lead.tasks.filter((task) => !task.is_done);
  const done = lead.tasks.filter((task) => task.is_done);

  const submit = () => {
    if (!title.trim()) return setError("Podaj treść zadania.");
    if (!dueAt) return setError("Podaj termin.");
    setError("");
    createTask.mutate(
      { lead: lead.id, title: title.trim(), due_at: new Date(dueAt).toISOString() },
      {
        onSuccess: () => {
          setTitle("");
          setDueAt(defaultDue());
          setAdding(false);
        },
        onError: () => setError("Nie udało się dodać zadania."),
      },
    );
  };

  return (
    <SectionCard
      title="Zadania"
      action={
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          className="font-mono text-[11px] uppercase tracking-wider text-[var(--color-primary)]"
        >
          <Plus size={12} className="mr-1 inline" />
          Dodaj
        </button>
      }
    >

      {adding && (
        <div className="mb-3 space-y-2">
          <input
            className="input-base"
            placeholder="Np. oddzwonić do prezesa"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              className="input-base"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <Button size="sm" onClick={submit} loading={createTask.isPending}>
              Zapisz
            </Button>
          </div>
          {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
        </div>
      )}

      {lead.tasks.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">Brak zadań.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {[...open, ...done].map((task) => (
            <li key={task.id} className="flex items-start gap-2">
              <button
                type="button"
                title={task.is_done ? "Cofnij wykonanie" : "Oznacz jako wykonane"}
                onClick={() =>
                  task.is_done ? reopenTask.mutate(task.id) : completeTask.mutate({ id: task.id })
                }
                className="mt-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              >
                {task.is_done ? <RotateCcw size={13} /> : <Check size={13} />}
              </button>
              <div className="min-w-0 flex-1">
                <div
                  className={
                    task.is_done
                      ? "text-[var(--color-text-muted)] line-through"
                      : "text-[var(--color-text-primary)]"
                  }
                >
                  {task.title}
                </div>
                <div
                  className={
                    task.is_overdue
                      ? "text-xs text-[var(--color-primary)]"
                      : "text-xs text-[var(--color-text-muted)]"
                  }
                >
                  {format(new Date(task.due_at), "dd.MM.yyyy HH:mm")}
                  {task.assigned_to_name ? ` · ${task.assigned_to_name}` : ""}
                  {task.is_overdue ? " · po terminie" : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => deleteTask.mutate(task.id)}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                title="Usuń zadanie"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
